/**
 * ImportCartillaModal.js - Modal para importar cartilla de vacunación desde foto
 * Permite subir una foto de la cartilla, revisar las vacunas extraídas,
 * agregar vacunas manualmente y confirmar la importación
 */
import React, { useState, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  analizarCartillaConIA,
  verificarDuplicadosVacunas,
  importarVacunasLote,
  importarDesparasitacionesLote,
  crearEntradaExpediente
} from '../services/api';
import catalogo from '../data/catalogoMedicamentos.json';

// Listas de nombres para los dropdowns (desde catálogo centralizado)
const VACUNAS_COMUNES = catalogo.vacunas.map(v => v.nombre);
const DESPARASITANTES_COMUNES = catalogo.desparasitantes.map(d => d.nombre);

// Generador de IDs únicos para items en el modal
let itemIdCounter = 0;
const generateItemId = () => `item_${Date.now()}_${++itemIdCounter}`;

/**
 * Determina si una vacuna es válida para importar
 * Válida = tiene fecha, tiene nombre, no es duplicada, no está omitida
 */
const isVacunaValida = (v) => {
  return !v.omitir && !v.esDuplicada && !!v.fecha && !!v.nombre;
};

/**
 * Obtiene la razón por la cual una vacuna no es válida
 * Retorna null si la vacuna es válida
 */
const getInvalidReason = (v) => {
  if (!v.nombre) return 'Sin nombre';
  if (!v.fecha) return 'Sin fecha';
  if (v.esDuplicada) return 'Ya existe';
  if (v.omitir) return 'Omitida';
  return null;
};

/**
 * Componente para renderizar una tarjeta de desparasitación
 * Muestra indicadores de validez y razón de invalidez
 */
function DesparasitacionCard({ desparasitacion, isValid, invalidReason, onDesparasitacionChange, onRemove }) {
  const formatDate = (fecha) => {
    if (!fecha) return null;
    return new Date(fecha + 'T12:00:00').toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div
      style={{
        border: isValid ? '1px solid #dbeafe' : '1px solid #fcd34d',
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '12px',
        backgroundColor: isValid ? '#eff6ff' : '#fffbeb',
      }}
    >
      {/* Header con indicador de validez, nombre y fecha */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px',
        flexWrap: 'wrap'
      }}>
        {/* Indicador de validez */}
        <span style={{
          color: isValid ? '#2563eb' : '#dc2626',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center'
        }}>
          <i className={`bi ${isValid ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`}></i>
        </span>

        {/* Nombre del producto */}
        <span style={{
          fontWeight: '500',
          fontSize: '14px',
          color: '#374151'
        }}>
          {desparasitacion.producto || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Sin producto</span>}
        </span>

        {/* Fecha o indicador de sin fecha */}
        {desparasitacion.fecha ? (
          <span style={{ fontSize: '12px', color: '#6b7280' }}>
            ({formatDate(desparasitacion.fecha)})
          </span>
        ) : (
          <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: '500' }}>
            — Sin fecha
          </span>
        )}

        {/* Badge de razón de invalidez */}
        {!isValid && invalidReason && invalidReason !== 'Sin fecha' && invalidReason !== 'Sin producto' && (
          <span style={{
            backgroundColor: '#fef3c7',
            color: '#92400e',
            fontSize: '10px',
            padding: '2px 6px',
            borderRadius: '4px',
            fontWeight: '600'
          }}>
            {invalidReason}
          </span>
        )}

        {/* Badge de origen (IA/Manual) */}
        <span style={{
          backgroundColor: desparasitacion.origenImportacion === 'ia' ? '#f3e8ff' : '#dcfce7',
          color: desparasitacion.origenImportacion === 'ia' ? '#7c3aed' : '#166534',
          fontSize: '10px',
          padding: '2px 6px',
          borderRadius: '4px',
          fontWeight: '600',
          marginLeft: 'auto'
        }}>
          {desparasitacion.origenImportacion === 'ia' ? 'IA' : 'Manual'}
        </span>
      </div>

      {/* Campos editables */}
      <div className="row g-2">
        <div className="col-6">
          <label style={{ fontSize: '11px', color: '#666' }}>Fecha *</label>
          <input
            type="date"
            className={`form-control form-control-sm ${!desparasitacion.fecha ? 'border-danger' : ''}`}
            value={desparasitacion.fecha || ''}
            onChange={(e) => onDesparasitacionChange(desparasitacion._id, 'fecha', e.target.value)}
          />
        </div>
        <div className="col-6">
          <label style={{ fontSize: '11px', color: '#666' }}>Producto *</label>
          <input
            type="text"
            className={`form-control form-control-sm ${!desparasitacion.producto ? 'border-danger' : ''}`}
            value={desparasitacion.producto || ''}
            onChange={(e) => onDesparasitacionChange(desparasitacion._id, 'producto', e.target.value)}
            placeholder="Ej: Nexgard"
          />
        </div>
        <div className="col-6">
          <label style={{ fontSize: '11px', color: '#666' }}>Laboratorio</label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={desparasitacion.laboratorio || ''}
            onChange={(e) => onDesparasitacionChange(desparasitacion._id, 'laboratorio', e.target.value)}
          />
        </div>
        <div className="col-6">
          <label style={{ fontSize: '11px', color: '#666' }}>Tipo</label>
          <select
            className="form-select form-select-sm"
            value={desparasitacion.tipo || ''}
            onChange={(e) => onDesparasitacionChange(desparasitacion._id, 'tipo', e.target.value)}
          >
            <option value="">Seleccionar...</option>
            <option value="interno">Interno</option>
            <option value="externo">Externo</option>
            <option value="ambos">Interno y externo</option>
          </select>
        </div>
        <div className="col-6">
          <label style={{ fontSize: '11px', color: '#666' }}>
            <i className="bi bi-calendar-event me-1" style={{ color: '#7c3aed' }}></i>
            Próxima dosis
          </label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={desparasitacion.proximaDosis || ''}
            onChange={(e) => onDesparasitacionChange(desparasitacion._id, 'proximaDosis', e.target.value)}
            style={desparasitacion.proximaDosis ? { borderColor: '#7c3aed', backgroundColor: '#faf5ff' } : {}}
          />
        </div>
      </div>

      <button
        className="btn btn-sm btn-outline-danger mt-2"
        onClick={() => onRemove(desparasitacion._id)}
        style={{ fontSize: '11px' }}
      >
        <i className="bi bi-trash me-1"></i>
        Quitar
      </button>
    </div>
  );
}

/**
 * Componente para renderizar una tarjeta de vacuna
 * Muestra indicadores de validez y razón de invalidez
 */
function VacunaCard({ vacuna, isValid, invalidReason, onVacunaChange, onRemove }) {
  const formatDate = (fecha) => {
    if (!fecha) return null;
    return new Date(fecha + 'T12:00:00').toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div
      style={{
        border: isValid ? '1px solid #d1fae5' : '1px solid #fcd34d',
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '12px',
        backgroundColor: isValid ? '#f0fdf4' : '#fffbeb',
      }}
    >
      {/* Header con indicador de validez, nombre y fecha */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px',
        flexWrap: 'wrap'
      }}>
        {/* Indicador de validez */}
        <span style={{
          color: isValid ? '#059669' : '#dc2626',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center'
        }}>
          <i className={`bi ${isValid ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`}></i>
        </span>

        {/* Nombre de la vacuna */}
        <span style={{
          fontWeight: '500',
          fontSize: '14px',
          color: '#374151'
        }}>
          {vacuna.nombre || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Sin nombre</span>}
        </span>

        {/* Fecha o indicador de sin fecha */}
        {vacuna.fecha ? (
          <span style={{ fontSize: '12px', color: '#6b7280' }}>
            ({formatDate(vacuna.fecha)})
          </span>
        ) : (
          <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: '500' }}>
            — Sin fecha
          </span>
        )}

        {/* Badge de razón de invalidez */}
        {!isValid && invalidReason && invalidReason !== 'Sin fecha' && invalidReason !== 'Sin nombre' && (
          <span style={{
            backgroundColor: '#fef3c7',
            color: '#92400e',
            fontSize: '10px',
            padding: '2px 6px',
            borderRadius: '4px',
            fontWeight: '600'
          }}>
            {invalidReason}
          </span>
        )}

        {/* Badge de origen (IA/Manual) */}
        <span style={{
          backgroundColor: vacuna.origenImportacion === 'ia' ? '#f3e8ff' : '#dcfce7',
          color: vacuna.origenImportacion === 'ia' ? '#7c3aed' : '#166534',
          fontSize: '10px',
          padding: '2px 6px',
          borderRadius: '4px',
          fontWeight: '600',
          marginLeft: 'auto'
        }}>
          {vacuna.origenImportacion === 'ia' ? 'IA' : 'Manual'}
        </span>
      </div>

      {/* Campos editables */}
      <div className="row g-2">
        <div className="col-6">
          <label style={{ fontSize: '11px', color: '#666' }}>Fecha *</label>
          <input
            type="date"
            className={`form-control form-control-sm ${!vacuna.fecha ? 'border-danger' : ''}`}
            value={vacuna.fecha || ''}
            onChange={(e) => onVacunaChange(vacuna._id, 'fecha', e.target.value)}
          />
        </div>
        <div className="col-6">
          <label style={{ fontSize: '11px', color: '#666' }}>Nombre *</label>
          <input
            type="text"
            className={`form-control form-control-sm ${!vacuna.nombre ? 'border-danger' : ''}`}
            value={vacuna.nombre || ''}
            onChange={(e) => onVacunaChange(vacuna._id, 'nombre', e.target.value)}
            placeholder="Ej: Rabia"
          />
        </div>
        <div className="col-6">
          <label style={{ fontSize: '11px', color: '#666' }}>Laboratorio</label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={vacuna.laboratorio || ''}
            onChange={(e) => onVacunaChange(vacuna._id, 'laboratorio', e.target.value)}
          />
        </div>
        <div className="col-6">
          <label style={{ fontSize: '11px', color: '#666' }}>Lote</label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={vacuna.lote || ''}
            onChange={(e) => onVacunaChange(vacuna._id, 'lote', e.target.value)}
          />
        </div>
        <div className="col-6">
          <label style={{ fontSize: '11px', color: '#666' }}>
            <i className="bi bi-calendar-event me-1" style={{ color: '#7c3aed' }}></i>
            Próxima vacunación
          </label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={vacuna.proximaVacunacion || ''}
            onChange={(e) => onVacunaChange(vacuna._id, 'proximaVacunacion', e.target.value)}
            style={vacuna.proximaVacunacion ? { borderColor: '#7c3aed', backgroundColor: '#faf5ff' } : {}}
          />
        </div>
      </div>

      <button
        className="btn btn-sm btn-outline-danger mt-2"
        onClick={() => onRemove(vacuna._id)}
        style={{ fontSize: '11px' }}
      >
        <i className="bi bi-trash me-1"></i>
        Quitar
      </button>
    </div>
  );
}

export default function ImportCartillaModal({ show, onClose, objetoId, onImportSuccess, onDesparasitacionesImportadas, desparasitacionesExistentes = [] }) {
  const [step, setStep] = useState('upload'); // 'upload' | 'analyzing' | 'review' | 'importing'
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  // Estado para vacunas y desparasitaciones - cada item tiene un _id único
  const [vacunas, setVacunas] = useState([]);
  const [desparasitaciones, setDesparasitaciones] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [notas, setNotas] = useState(null);
  const [error, setError] = useState(null);
  const [importing, setImporting] = useState(false);
  const importingRef = useRef(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addFormType, setAddFormType] = useState('vacuna'); // 'vacuna' | 'desparasitacion'
  const [newVacuna, setNewVacuna] = useState({
    fecha: '',
    nombre: '',
    laboratorio: '',
    lote: '',
    caducidad: '',
    proximaVacunacion: ''
  });
  const [newDesparasitacion, setNewDesparasitacion] = useState({
    fecha: '',
    producto: '',
    laboratorio: '',
    tipo: '',
    proximaDosis: ''
  });
  const fileInputRef = useRef(null);

  if (!show) return null;

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Solo se permiten archivos JPG, PNG o PDF');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('El archivo es demasiado grande. Maximo 10MB.');
      return;
    }

    setSelectedFile(file);
    setError(null);

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreviewUrl(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setPreviewUrl(null);
    }
  };

  const handleAnalyze = async () => {
    if (!selectedFile) return;

    setStep('analyzing');
    setError(null);

    try {
      const result = await analizarCartillaConIA(objetoId, selectedFile);

      if (result.success) {
        // Agregar ID único y origen a cada vacuna detectada
        const vacunasConId = (result.vacunas || []).map(v => ({
          ...v,
          _id: generateItemId(),
          origenImportacion: 'ia'
        }));

        // Agregar ID único y origen a cada desparasitacion detectada
        const desparasitacionesConId = (result.desparasitaciones || []).map(d => ({
          ...d,
          _id: generateItemId(),
          origenImportacion: 'ia'
        }));

        // Verificar duplicados de vacunas
        const conDuplicados = await verificarDuplicadosVacunas(objetoId, vacunasConId);

        if (conDuplicados.error) {
          setVacunas(vacunasConId.map(v => ({ ...v, esDuplicada: false })));
        } else {
          // Preservar los _ids originales
          const vacunasConDuplicados = (conDuplicados.data || vacunasConId).map((v, i) => ({
            ...v,
            _id: vacunasConId[i]?._id || generateItemId()
          }));
          setVacunas(vacunasConDuplicados);
        }

        // Verificar duplicados de desparasitaciones contra las existentes (ahora de subcolección)
        const desparasitacionesConDuplicados = desparasitacionesConId.map(d => {
          if (!d.fecha || !d.producto) return { ...d, esDuplicada: false };
          const existe = desparasitacionesExistentes.some(existente =>
            existente.fecha?.substring(0, 10) === d.fecha?.substring(0, 10) &&
            (existente.producto || existente.titulo || '').toLowerCase().trim() === d.producto?.toLowerCase().trim()
          );
          return { ...d, esDuplicada: existe };
        });
        setDesparasitaciones(desparasitacionesConDuplicados);

        setWarnings(result.warnings || []);
        setNotas(result.notas || null);
        setStep('review');
      } else {
        // Guardar el error completo para mostrar información detallada
        setError({
          errorType: result.errorType || 'unknown',
          message: result.message || 'Error al analizar la cartilla',
          suggestions: result.suggestions || [],
          resetInMinutes: result.resetInMinutes,
          limit: result.limit
        });
        setStep('upload');
      }
    } catch (err) {
      console.error('Error analizando cartilla:', err);
      // Intentar parsear error de rate limit del servidor
      if (err.response?.status === 429 || err.errorType === 'rate_limit') {
        setError({
          errorType: 'rate_limit',
          message: err.message || 'Has alcanzado el límite de análisis con IA.',
          suggestions: err.suggestions || ['Puedes agregar las vacunas manualmente mientras tanto'],
          resetInMinutes: err.resetInMinutes || 60,
          limit: err.limit || 5
        });
      } else {
        setError({
          errorType: 'error',
          message: 'Error al procesar la imagen. Por favor intenta de nuevo.',
          suggestions: []
        });
      }
      setStep('upload');
    }
  };

  // Saltar directamente al modo de revisión para agregar vacunas manualmente
  const handleSkipToManual = () => {
    setStep('review');
    setError(null);
    setVacunas([]);
    setShowAddForm(true);
  };

  // Actualizar una vacuna por su ID único
  const handleVacunaChange = (vacunaId, field, value) => {
    setVacunas(prev => prev.map(v =>
      v._id === vacunaId ? { ...v, [field]: value } : v
    ));
  };

  // Eliminar una vacuna por su ID único
  const handleRemoveVacuna = (vacunaId) => {
    setVacunas(prev => prev.filter(v => v._id !== vacunaId));
  };

  // Actualizar una desparasitación por su ID único
  const handleDesparasitacionChange = (despId, field, value) => {
    setDesparasitaciones(prev => prev.map(d =>
      d._id === despId ? { ...d, [field]: value } : d
    ));
  };

  // Eliminar una desparasitación por su ID único
  const handleRemoveDesparasitacion = (despId) => {
    setDesparasitaciones(prev => prev.filter(d => d._id !== despId));
  };

  // Determina si una desparasitación es válida para importar
  const isDesparasitacionValida = (d) => {
    return !d.omitir && !d.esDuplicada && !!d.fecha && !!d.producto;
  };

  // Obtiene la razón por la cual una desparasitación no es válida
  const getDesparasitacionInvalidReason = (d) => {
    if (!d.producto) return 'Sin producto';
    if (!d.fecha) return 'Sin fecha';
    if (d.esDuplicada) return 'Ya existe';
    if (d.omitir) return 'Omitida';
    return null;
  };

  const handleAddManualVacuna = async () => {
    if (!newVacuna.nombre) {
      toast.error('El nombre de la vacuna es requerido');
      return;
    }

    const vacunaToAdd = {
      ...newVacuna,
      _id: generateItemId(),
      origenImportacion: 'manual',
      esDuplicada: false
    };

    // Verificar si es duplicada
    if (newVacuna.fecha && newVacuna.nombre) {
      const checkResult = await verificarDuplicadosVacunas(objetoId, [vacunaToAdd]);
      if (!checkResult.error && checkResult.data?.[0]) {
        vacunaToAdd.esDuplicada = checkResult.data[0].esDuplicada;
        vacunaToAdd.vacunaExistenteId = checkResult.data[0].vacunaExistenteId;
      }
    }

    setVacunas(prev => [...prev, vacunaToAdd]);
    setNewVacuna({
      fecha: '',
      nombre: '',
      laboratorio: '',
      lote: '',
      caducidad: '',
      proximaVacunacion: ''
    });
    setShowAddForm(false);
    toast.success('Vacuna agregada');
  };

  const handleAddManualDesparasitacion = () => {
    if (!newDesparasitacion.producto) {
      toast.error('El nombre del producto es requerido');
      return;
    }

    const despToAdd = {
      ...newDesparasitacion,
      _id: generateItemId(),
      origenImportacion: 'manual'
    };

    setDesparasitaciones(prev => [...prev, despToAdd]);
    setNewDesparasitacion({
      fecha: '',
      producto: '',
      laboratorio: '',
      tipo: '',
      proximaDosis: ''
    });
    setShowAddForm(false);
    toast.success('Desparasitacion agregada');
  };

  // Separar vacunas válidas de inválidas
  const vacunasValidas = vacunas.filter(isVacunaValida);
  const vacunasInvalidas = vacunas.filter(v => !isVacunaValida(v));

  // Separar desparasitaciones válidas de inválidas
  const desparasitacionesValidas = desparasitaciones.filter(isDesparasitacionValida);
  const desparasitacionesInvalidas = desparasitaciones.filter(d => !isDesparasitacionValida(d));

  // Alias para mantener compatibilidad
  const vacunasParaImportar = vacunasValidas;
  const desparasitacionesParaImportar = desparasitacionesValidas;

  // Total de items para importar
  const totalItemsParaImportar = vacunasParaImportar.length + desparasitacionesParaImportar.length;

  const handleImport = async () => {
    if (totalItemsParaImportar === 0) {
      toast.error('No hay vacunas ni desparasitaciones para importar');
      return;
    }

    // Evitar doble click con ref (setState es async, no es suficiente como guard)
    if (importingRef.current) return;
    importingRef.current = true;
    setImporting(true);
    setStep('importing');

    try {
      // 1. Agrupar vacunas por fecha para crear visitas médicas
      // Normalizar fechas a YYYY-MM-DD para evitar duplicados por formato
      const normalizeFecha = (f) => {
        if (!f) return '';
        const d = new Date(f.includes('T') ? f : f + 'T12:00:00');
        if (isNaN(d.getTime())) return f;
        return d.toISOString().split('T')[0];
      };
      const vacunasPorFecha = {};
      vacunasParaImportar.forEach(vacuna => {
        const fecha = normalizeFecha(vacuna.fecha);
        if (!vacunasPorFecha[fecha]) {
          vacunasPorFecha[fecha] = [];
        }
        vacunasPorFecha[fecha].push(vacuna);
      });

      const fechasVacunas = Object.keys(vacunasPorFecha).sort();
      const vacunasConVisita = [];
      const visitasCreadas = [];

      // 2. Para cada fecha de vacunas, crear visita médica
      for (const fecha of fechasVacunas) {
        const vacunasDeFecha = vacunasPorFecha[fecha];
        const nombresVacunas = vacunasDeFecha.map(v => v.nombre).join(', ');

        let notasVisita = `Vacunas aplicadas: ${nombresVacunas}`;
        const proximas = vacunasDeFecha.filter(v => v.proximaVacunacion);
        if (proximas.length > 0) {
          const formatFecha = (f) => new Date(f + 'T12:00:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
          notasVisita += '\nPróxima vacunación: ' + proximas.map(v => `${v.nombre} → ${formatFecha(v.proximaVacunacion)}`).join(', ');
        }
        notasVisita += '\n\n(Importado desde foto de cartilla)';

        const entryData = {
          fecha: `${fecha}T12:00`,
          tipos: ['vacunacion'],
          tipo: 'Vacunación',
          notas: notasVisita,
          importadaDeCartilla: true,
          nombreVacuna: vacunasDeFecha[0].nombre,
          laboratorio: vacunasDeFecha[0].laboratorio || '',
          numeroLote: vacunasDeFecha[0].lote || ''
        };

        const resp = await crearEntradaExpediente(objetoId, entryData);
        if (resp.error) {
          console.error(`[ImportCartilla] Error creando visita para fecha ${fecha}:`, resp.error);
          continue;
        }

        const visitaId = resp.data?.id;
        visitasCreadas.push({ fecha, visitaId, tipos: ['vacunacion'], vacunas: vacunasDeFecha.length });

        for (const vacuna of vacunasDeFecha) {
          vacunasConVisita.push({ ...vacuna, eventoMedicoId: visitaId });
        }
      }

      // 3. Importar vacunas a la subcolección Vacunas
      let numVacunasImportadas = 0;
      let numVacunasOmitidas = 0;

      if (vacunasConVisita.length > 0) {
        const vacunasSinId = vacunasConVisita.map(({ _id, ...rest }) => rest);
        const result = await importarVacunasLote(objetoId, vacunasSinId);
        if (result.error) {
          console.error('[ImportCartilla] Error importando vacunas:', result.error);
        } else {
          numVacunasImportadas = result.data?.length || 0;
          numVacunasOmitidas = result.skipped?.length || 0;
        }
      }

      // 4. Importar desparasitaciones a la subcolección Desparasitaciones
      let numDesparasitacionesImportadas = 0;
      let desparasitacionesCreadas = [];

      if (desparasitacionesParaImportar.length > 0) {
        // Auto-fill tipoDesparasitacion from catalog
        const despParaCrear = desparasitacionesParaImportar.map(d => {
          let tipoDesp = d.tipo || null;
          if (!tipoDesp) {
            const match = catalogo.desparasitantes.find(
              cat => cat.nombre.toLowerCase() === (d.producto || '').toLowerCase()
            );
            if (match) tipoDesp = match.tipo;
          }
          return {
            fecha: d.fecha,
            producto: d.producto,
            tipoDesparasitacion: tipoDesp,
            proximaDesparasitacion: d.proximaDosis || null,
            laboratorio: d.laboratorio || null,
            importadaDeCartilla: true,
            origenImportacion: d.origenImportacion || 'ia'
          };
        });

        const result = await importarDesparasitacionesLote(objetoId, despParaCrear);
        if (result.error) {
          console.error('[ImportCartilla] Error importando desparasitaciones:', result.error);
        } else {
          numDesparasitacionesImportadas = result.data?.length || 0;
          desparasitacionesCreadas = result.data || [];
        }
      }

      // 5. Mostrar resumen
      if (numVacunasImportadas > 0 || numDesparasitacionesImportadas > 0) {
        let mensaje = '';
        if (numVacunasImportadas > 0) {
          mensaje += `${numVacunasImportadas} vacuna${numVacunasImportadas !== 1 ? 's' : ''}`;
        }
        if (numDesparasitacionesImportadas > 0) {
          mensaje += (mensaje ? ' y ' : '') + `${numDesparasitacionesImportadas} desparasitacion${numDesparasitacionesImportadas !== 1 ? 'es' : ''}`;
        }
        mensaje += ` importada${(numVacunasImportadas + numDesparasitacionesImportadas) !== 1 ? 's' : ''}`;

        toast.success(mensaje);
        onImportSuccess(vacunasConVisita, visitasCreadas);
        if (onDesparasitacionesImportadas && desparasitacionesCreadas.length > 0) {
          onDesparasitacionesImportadas(desparasitacionesCreadas);
        }
      }

      if (numVacunasOmitidas > 0) {
        toast(`${numVacunasOmitidas} vacuna${numVacunasOmitidas !== 1 ? 's' : ''} omitida${numVacunasOmitidas !== 1 ? 's' : ''} por duplicada${numVacunasOmitidas !== 1 ? 's' : ''}`);
      }

      if (numVacunasImportadas === 0 && numDesparasitacionesImportadas === 0) {
        toast.error('No se pudieron importar los datos');
        importingRef.current = false;
        setImporting(false);
        setStep('review');
        return;
      }

      handleClose();
    } catch (err) {
      console.error('Error importando:', err);
      setError('Error al importar los datos');
      importingRef.current = false;
      setImporting(false);
      setStep('review');
    }
  };

  const handleClose = () => {
    setStep('upload');
    importingRef.current = false;
    setImporting(false);
    setSelectedFile(null);
    setPreviewUrl(null);
    setVacunas([]);
    setDesparasitaciones([]);
    setWarnings([]);
    setNotas(null);
    setError(null);
    setShowAddForm(false);
    setAddFormType('vacuna');
    setNewVacuna({ fecha: '', nombre: '', laboratorio: '', lote: '', caducidad: '', proximaVacunacion: '' });
    setNewDesparasitacion({ fecha: '', producto: '', laboratorio: '', tipo: '', proximaDosis: '' });
    onClose();
  };

  const handleRescan = () => {
    setStep('upload');
    setSelectedFile(null);
    setPreviewUrl(null);
    setVacunas([]);
    setDesparasitaciones([]);
    setWarnings([]);
    setNotas(null);
    setError(null);
    setShowAddForm(false);
    setAddFormType('vacuna');
  };

  return (
    <div className="modal-overlay import-cartilla-overlay" onClick={handleClose}>
      <div
        className="modal-content import-cartilla-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>Importar Cartilla de Vacunación</h3>
          <button className="modal-close" onClick={handleClose}>&times;</button>
        </div>

        <div className="modal-body" style={{ padding: '20px' }}>
          {/* Step: Upload */}
          {step === 'upload' && (
            <>
              {error && (
                <div style={{
                  backgroundColor: error.errorType === 'wrong_document' ? '#fef3c7' :
                                   error.errorType === 'rate_limit' ? '#fffbeb' : '#fef2f2',
                  border: `1px solid ${error.errorType === 'wrong_document' ? '#fcd34d' :
                                        error.errorType === 'rate_limit' ? '#fcd34d' : '#fecaca'}`,
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '16px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <span style={{ fontSize: '24px' }}>
                      {error.errorType === 'wrong_document' ? '📄' :
                       error.errorType === 'unrecognized_document' ? '❓' :
                       error.errorType === 'rate_limit' ? '⏳' :
                       error.errorType === 'ilegible' ? '⚠️' : '❌'}
                    </span>
                    <div style={{ flex: 1 }}>
                      <h4 style={{
                        margin: '0 0 8px 0',
                        color: error.errorType === 'wrong_document' ? '#d97706' :
                               error.errorType === 'unrecognized_document' ? '#d97706' :
                               error.errorType === 'rate_limit' ? '#d97706' : '#dc2626',
                        fontSize: '15px'
                      }}>
                        {error.errorType === 'wrong_document' ? 'Documento incorrecto' :
                         error.errorType === 'unrecognized_document' ? 'Documento no reconocido' :
                         error.errorType === 'rate_limit' ? 'Limite alcanzado' :
                         error.errorType === 'ilegible' ? 'Imagen no legible' :
                         error.errorType === 'vision_api_not_enabled' ? 'Servicio no disponible' :
                         'Error al analizar'}
                      </h4>
                      <p style={{ margin: '0', color: '#555', fontSize: '14px' }}>
                        {error.message}
                      </p>
                      {error.suggestions && error.suggestions.length > 0 && (
                        <ul style={{
                          margin: '12px 0 0 0',
                          paddingLeft: '20px',
                          color: '#666',
                          fontSize: '13px'
                        }}>
                          {error.suggestions.map((s, i) => (
                            <li key={i}>{s}</li>
                          ))}
                        </ul>
                      )}
                      {/* Botones según el tipo de error */}
                      <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                        {(error.errorType === 'wrong_document' || error.errorType === 'unrecognized_document') && (
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            className="btn btn-outline-warning"
                            style={{ fontSize: '13px' }}
                          >
                            <i className="bi bi-arrow-repeat me-2"></i>
                            Cambiar archivo
                          </button>
                        )}
                        {(error.errorType === 'rate_limit' ||
                          error.errorType === 'vision_api_not_enabled' ||
                          error.errorType === 'quota_exceeded' ||
                          error.errorType === 'unrecognized_document') && (
                          <button
                            onClick={handleSkipToManual}
                            className="btn btn-warning"
                            style={{ fontSize: '13px' }}
                          >
                            <i className="bi bi-pencil me-2"></i>
                            Agregar manualmente
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <p style={{ color: '#666', fontSize: '14px', marginBottom: '16px' }}>
                  Sube una foto de la cartilla de vacunación y extraeremos las vacunas automáticamente.
                </p>

                {previewUrl ? (
                  <div style={{ marginBottom: '16px' }}>
                    <img
                      src={previewUrl}
                      alt="Preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '200px',
                        borderRadius: '8px',
                        border: '1px solid #ddd'
                      }}
                    />
                    <div style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
                      {selectedFile?.name}
                    </div>
                  </div>
                ) : selectedFile ? (
                  <div style={{
                    backgroundColor: '#f3f4f6',
                    borderRadius: '8px',
                    padding: '20px',
                    marginBottom: '16px'
                  }}>
                    <i className="bi bi-file-earmark-pdf" style={{ fontSize: '32px', color: '#dc2626' }}></i>
                    <div style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
                      {selectedFile?.name}
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      border: '2px dashed #ddd',
                      borderRadius: '12px',
                      padding: '40px 20px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.borderColor = '#4CAF50'}
                    onMouseOut={(e) => e.currentTarget.style.borderColor = '#ddd'}
                  >
                    <i className="bi bi-cloud-upload" style={{ fontSize: '40px', color: '#4CAF50' }}></i>
                    <p style={{ margin: '12px 0 0', color: '#666' }}>
                      Haz clic para seleccionar una imagen o PDF
                    </p>
                  </div>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,application/pdf"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                {selectedFile && (
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => fileInputRef.current?.click()}
                    style={{ flex: 1 }}
                  >
                    Cambiar archivo
                  </button>
                )}
                <button
                  className="btn btn-primary"
                  onClick={selectedFile ? handleAnalyze : () => fileInputRef.current?.click()}
                  style={{ flex: selectedFile ? 1 : 'auto', width: selectedFile ? 'auto' : '100%' }}
                >
                  {selectedFile ? (
                    <>
                      <i className="bi bi-search me-2"></i>
                      Analizar Foto
                    </>
                  ) : (
                    <>
                      <i className="bi bi-upload me-2"></i>
                      Seleccionar archivo
                    </>
                  )}
                </button>
              </div>

              <div style={{
                marginTop: '16px',
                padding: '12px',
                backgroundColor: '#fffbeb',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#92400e'
              }}>
                <i className="bi bi-lightbulb me-2"></i>
                <strong>Tip:</strong> Para mejores resultados, asegurate que la foto tenga buena iluminacion y que se vean claramente las etiquetas de vacunas (incluso si estan inclinadas).
              </div>
            </>
          )}

          {/* Step: Analyzing */}
          {step === 'analyzing' && (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div className="spinner-border text-primary mb-3" role="status" style={{ width: '48px', height: '48px' }}>
                <span className="visually-hidden">Analizando...</span>
              </div>
              <p style={{ color: '#666', fontSize: '14px' }}>
                Analizando la cartilla de vacunación...
              </p>
              <p style={{ color: '#999', fontSize: '13px' }}>
                Esto puede tomar unos segundos
              </p>
            </div>
          )}

          {/* Step: Review */}
          {step === 'review' && (
            <>
              {warnings.length > 0 && (
                <div style={{
                  backgroundColor: '#fffbeb',
                  border: '1px solid #fcd34d',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px'
                }}>
                  {warnings.map((w, i) => (
                    <div key={i} style={{ color: '#92400e', fontSize: '13px', marginBottom: i < warnings.length - 1 ? '4px' : 0 }}>
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      {w}
                    </div>
                  ))}
                </div>
              )}

              {notas && (
                <div style={{
                  backgroundColor: '#f0f9ff',
                  border: '1px solid #bae6fd',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px',
                  fontSize: '13px',
                  color: '#0369a1'
                }}>
                  <i className="bi bi-info-circle me-2"></i>
                  {notas}
                </div>
              )}

              <div style={{ maxHeight: '350px', overflowY: 'auto', marginBottom: '16px' }}>
                {/* Sección de vacunas válidas */}
                {vacunasValidas.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '12px',
                      color: '#059669',
                      fontWeight: '600',
                      fontSize: '14px'
                    }}>
                      <i className="bi bi-check-circle-fill"></i>
                      Vacunas para importar ({vacunasValidas.length})
                    </div>

                    {vacunasValidas.map((vacuna) => (
                      <VacunaCard
                        key={vacuna._id}
                        vacuna={vacuna}
                        isValid={true}
                        onVacunaChange={handleVacunaChange}
                        onRemove={handleRemoveVacuna}
                      />
                    ))}
                  </div>
                )}

                {/* Separador si hay ambos tipos */}
                {vacunasValidas.length > 0 && vacunasInvalidas.length > 0 && (
                  <div style={{
                    borderTop: '1px solid #e5e7eb',
                    margin: '16px 0',
                  }} />
                )}

                {/* Sección de vacunas inválidas */}
                {vacunasInvalidas.length > 0 && (
                  <div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px',
                      color: '#d97706',
                      fontWeight: '600',
                      fontSize: '14px'
                    }}>
                      <i className="bi bi-exclamation-triangle-fill"></i>
                      No se pueden importar ({vacunasInvalidas.length})
                    </div>
                    <p style={{
                      color: '#92400e',
                      fontSize: '12px',
                      marginBottom: '12px',
                      backgroundColor: '#fffbeb',
                      padding: '8px 12px',
                      borderRadius: '6px'
                    }}>
                      Completa los datos faltantes para poder importar estas vacunas
                    </p>

                    {vacunasInvalidas.map((vacuna) => (
                      <VacunaCard
                        key={vacuna._id}
                        vacuna={vacuna}
                        isValid={false}
                        invalidReason={getInvalidReason(vacuna)}
                        onVacunaChange={handleVacunaChange}
                        onRemove={handleRemoveVacuna}
                      />
                    ))}
                  </div>
                )}

                {/* Separador entre vacunas y desparasitaciones */}
                {vacunas.length > 0 && desparasitaciones.length > 0 && (
                  <div style={{
                    borderTop: '2px solid #e5e7eb',
                    margin: '20px 0 16px 0',
                    paddingTop: '16px',
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: '#2563eb',
                      fontWeight: '600',
                      fontSize: '15px',
                      marginBottom: '4px'
                    }}>
                      <i className="bi bi-capsule"></i>
                      Desparasitaciones
                    </div>
                  </div>
                )}

                {/* Sección de desparasitaciones válidas */}
                {desparasitacionesValidas.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    {vacunas.length === 0 && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: '#2563eb',
                        fontWeight: '600',
                        fontSize: '15px',
                        marginBottom: '12px'
                      }}>
                        <i className="bi bi-capsule"></i>
                        Desparasitaciones
                      </div>
                    )}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '12px',
                      color: '#2563eb',
                      fontWeight: '600',
                      fontSize: '14px'
                    }}>
                      <i className="bi bi-check-circle-fill"></i>
                      Para importar ({desparasitacionesValidas.length})
                    </div>

                    {desparasitacionesValidas.map((desp) => (
                      <DesparasitacionCard
                        key={desp._id}
                        desparasitacion={desp}
                        isValid={true}
                        onDesparasitacionChange={handleDesparasitacionChange}
                        onRemove={handleRemoveDesparasitacion}
                      />
                    ))}
                  </div>
                )}

                {/* Separador si hay ambos tipos de desparasitaciones */}
                {desparasitacionesValidas.length > 0 && desparasitacionesInvalidas.length > 0 && (
                  <div style={{
                    borderTop: '1px solid #e5e7eb',
                    margin: '16px 0',
                  }} />
                )}

                {/* Sección de desparasitaciones inválidas */}
                {desparasitacionesInvalidas.length > 0 && (
                  <div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px',
                      color: '#d97706',
                      fontWeight: '600',
                      fontSize: '14px'
                    }}>
                      <i className="bi bi-exclamation-triangle-fill"></i>
                      Desparasitaciones incompletas ({desparasitacionesInvalidas.length})
                    </div>
                    <p style={{
                      color: '#92400e',
                      fontSize: '12px',
                      marginBottom: '12px',
                      backgroundColor: '#fffbeb',
                      padding: '8px 12px',
                      borderRadius: '6px'
                    }}>
                      Completa los datos faltantes para poder importar
                    </p>

                    {desparasitacionesInvalidas.map((desp) => (
                      <DesparasitacionCard
                        key={desp._id}
                        desparasitacion={desp}
                        isValid={false}
                        invalidReason={getDesparasitacionInvalidReason(desp)}
                        onDesparasitacionChange={handleDesparasitacionChange}
                        onRemove={handleRemoveDesparasitacion}
                      />
                    ))}
                  </div>
                )}

                {vacunas.length === 0 && desparasitaciones.length === 0 && !showAddForm && (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                    No se encontraron vacunas ni desparasitaciones en la imagen
                  </div>
                )}
              </div>

              {/* Formulario para agregar vacuna o desparasitacion manualmente */}
              {showAddForm ? (
                <div style={{
                  border: `2px dashed ${addFormType === 'vacuna' ? '#10b981' : '#3b82f6'}`,
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px',
                  backgroundColor: addFormType === 'vacuna' ? '#f0fdf4' : '#eff6ff'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <span style={{ fontWeight: '600', fontSize: '14px', color: addFormType === 'vacuna' ? '#166534' : '#1d4ed8' }}>
                      <i className={`bi ${addFormType === 'vacuna' ? 'bi-plus-circle' : 'bi-capsule'} me-2`}></i>
                      Agregar {addFormType === 'vacuna' ? 'vacuna' : 'desparasitacion'}
                    </span>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setShowAddForm(false)}
                      style={{ fontSize: '11px', padding: '2px 8px' }}
                    >
                      Cancelar
                    </button>
                  </div>

                  {/* Toggle entre vacuna y desparasitacion */}
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                    <button
                      className={`btn btn-sm ${addFormType === 'vacuna' ? 'btn-success' : 'btn-outline-secondary'}`}
                      onClick={() => setAddFormType('vacuna')}
                      style={{ flex: 1, fontSize: '12px' }}
                    >
                      <i className="bi bi-shield-plus me-1"></i>
                      Vacuna
                    </button>
                    <button
                      className={`btn btn-sm ${addFormType === 'desparasitacion' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setAddFormType('desparasitacion')}
                      style={{ flex: 1, fontSize: '12px' }}
                    >
                      <i className="bi bi-capsule me-1"></i>
                      Desparasitacion
                    </button>
                  </div>

                  {/* Formulario de vacuna */}
                  {addFormType === 'vacuna' && (
                    <>
                      <div className="row g-2">
                        <div className="col-6">
                          <label style={{ fontSize: '11px', color: '#666' }}>Nombre *</label>
                          <select
                            className="form-select form-select-sm"
                            value={VACUNAS_COMUNES.includes(newVacuna.nombre) ? newVacuna.nombre : '__otro__'}
                            onChange={(e) => {
                              if (e.target.value === '__otro__') {
                                setNewVacuna(prev => ({ ...prev, nombre: '' }));
                              } else {
                                setNewVacuna(prev => ({ ...prev, nombre: e.target.value }));
                              }
                            }}
                          >
                            <option value="">Seleccionar...</option>
                            {VACUNAS_COMUNES.map(v => (
                              <option key={v} value={v}>{v}</option>
                            ))}
                            <option value="__otro__">Otra...</option>
                          </select>
                          {(!VACUNAS_COMUNES.includes(newVacuna.nombre) && newVacuna.nombre !== '') && (
                            <input
                              type="text"
                              className="form-control form-control-sm mt-1"
                              value={newVacuna.nombre}
                              onChange={(e) => setNewVacuna(prev => ({ ...prev, nombre: e.target.value }))}
                              placeholder="Nombre de la vacuna"
                            />
                          )}
                        </div>
                        <div className="col-6">
                          <label style={{ fontSize: '11px', color: '#666' }}>Fecha *</label>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={newVacuna.fecha}
                            onChange={(e) => setNewVacuna(prev => ({ ...prev, fecha: e.target.value }))}
                          />
                        </div>
                        <div className="col-6">
                          <label style={{ fontSize: '11px', color: '#666' }}>Laboratorio</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={newVacuna.laboratorio}
                            onChange={(e) => setNewVacuna(prev => ({ ...prev, laboratorio: e.target.value }))}
                          />
                        </div>
                        <div className="col-6">
                          <label style={{ fontSize: '11px', color: '#666' }}>Lote</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={newVacuna.lote}
                            onChange={(e) => setNewVacuna(prev => ({ ...prev, lote: e.target.value }))}
                          />
                        </div>
                      </div>

                      <button
                        className="btn btn-sm btn-success mt-2 w-100"
                        onClick={handleAddManualVacuna}
                        disabled={!newVacuna.nombre || !newVacuna.fecha}
                      >
                        <i className="bi bi-check-lg me-1"></i>
                        Agregar vacuna
                      </button>
                    </>
                  )}

                  {/* Formulario de desparasitacion */}
                  {addFormType === 'desparasitacion' && (
                    <>
                      <div className="row g-2">
                        <div className="col-6">
                          <label style={{ fontSize: '11px', color: '#666' }}>Producto *</label>
                          <select
                            className="form-select form-select-sm"
                            value={DESPARASITANTES_COMUNES.includes(newDesparasitacion.producto) ? newDesparasitacion.producto : '__otro__'}
                            onChange={(e) => {
                              if (e.target.value === '__otro__') {
                                setNewDesparasitacion(prev => ({ ...prev, producto: '' }));
                              } else {
                                setNewDesparasitacion(prev => ({ ...prev, producto: e.target.value }));
                              }
                            }}
                          >
                            <option value="">Seleccionar...</option>
                            {DESPARASITANTES_COMUNES.map(d => (
                              <option key={d} value={d}>{d}</option>
                            ))}
                            <option value="__otro__">Otro...</option>
                          </select>
                          {(!DESPARASITANTES_COMUNES.includes(newDesparasitacion.producto) && newDesparasitacion.producto !== '') && (
                            <input
                              type="text"
                              className="form-control form-control-sm mt-1"
                              value={newDesparasitacion.producto}
                              onChange={(e) => setNewDesparasitacion(prev => ({ ...prev, producto: e.target.value }))}
                              placeholder="Nombre del producto"
                            />
                          )}
                        </div>
                        <div className="col-6">
                          <label style={{ fontSize: '11px', color: '#666' }}>Fecha *</label>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={newDesparasitacion.fecha}
                            onChange={(e) => setNewDesparasitacion(prev => ({ ...prev, fecha: e.target.value }))}
                          />
                        </div>
                        <div className="col-6">
                          <label style={{ fontSize: '11px', color: '#666' }}>Laboratorio</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={newDesparasitacion.laboratorio}
                            onChange={(e) => setNewDesparasitacion(prev => ({ ...prev, laboratorio: e.target.value }))}
                          />
                        </div>
                        <div className="col-6">
                          <label style={{ fontSize: '11px', color: '#666' }}>Tipo</label>
                          <select
                            className="form-select form-select-sm"
                            value={newDesparasitacion.tipo}
                            onChange={(e) => setNewDesparasitacion(prev => ({ ...prev, tipo: e.target.value }))}
                          >
                            <option value="">Seleccionar...</option>
                            <option value="interno">Interno</option>
                            <option value="externo">Externo</option>
                            <option value="ambos">Interno y externo</option>
                          </select>
                        </div>
                      </div>

                      <button
                        className="btn btn-sm btn-primary mt-2 w-100"
                        onClick={handleAddManualDesparasitacion}
                        disabled={!newDesparasitacion.producto || !newDesparasitacion.fecha}
                      >
                        <i className="bi bi-check-lg me-1"></i>
                        Agregar desparasitacion
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <button
                  className="btn btn-outline-success w-100 mb-3"
                  onClick={() => setShowAddForm(true)}
                  style={{ fontSize: '13px' }}
                >
                  <i className="bi bi-plus-circle me-2"></i>
                  Agregar manualmente
                </button>
              )}

              {vacunas.length === 0 && desparasitaciones.length === 0 && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#fef3c7',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  fontSize: '13px',
                  color: '#92400e'
                }}>
                  <i className="bi bi-info-circle me-2"></i>
                  No se detectaron vacunas ni desparasitaciones automaticamente. Puedes agregarlas manualmente usando el boton de arriba.
                </div>
              )}

              {/* Próximas Citas Detectadas (solo futuras) */}
              {(() => {
                const hoy = new Date().toISOString().split('T')[0];
                const proximasCitas = [];
                vacunas.forEach(v => {
                  if (v.proximaVacunacion && v.proximaVacunacion > hoy) {
                    proximasCitas.push({
                      fecha: v.proximaVacunacion,
                      descripcion: v.nombre || 'Vacuna',
                      tipo: 'vacuna'
                    });
                  }
                });
                desparasitaciones.forEach(d => {
                  if (d.proximaDosis && d.proximaDosis > hoy) {
                    proximasCitas.push({
                      fecha: d.proximaDosis,
                      descripcion: d.producto || 'Desparasitación',
                      tipo: 'desparasitacion'
                    });
                  }
                });

                // Deduplicar por fecha
                const citasPorFecha = {};
                proximasCitas.forEach(c => {
                  if (!citasPorFecha[c.fecha]) {
                    citasPorFecha[c.fecha] = [];
                  }
                  citasPorFecha[c.fecha].push(c.descripcion);
                });

                const fechasUnicas = Object.keys(citasPorFecha).sort();
                if (fechasUnicas.length === 0) return null;

                const formatDate = (fecha) => {
                  return new Date(fecha + 'T12:00:00').toLocaleDateString('es-MX', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  });
                };

                return (
                  <div style={{
                    backgroundColor: '#faf5ff',
                    border: '1px solid #d8b4fe',
                    borderRadius: '8px',
                    padding: '12px',
                    marginBottom: '16px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px',
                      color: '#7c3aed',
                      fontWeight: '600',
                      fontSize: '14px'
                    }}>
                      <i className="bi bi-bell"></i>
                      Próximas citas detectadas
                    </div>
                    <p style={{ fontSize: '12px', color: '#6b21a8', margin: '0 0 8px 0' }}>
                      Se detectaron fechas de próximas vacunaciones. Después de importar, puedes crear recordatorios para estas fechas.
                    </p>
                    {fechasUnicas.map(fecha => (
                      <div key={fecha} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '6px 8px',
                        backgroundColor: '#fff',
                        borderRadius: '6px',
                        marginBottom: '4px',
                        fontSize: '13px'
                      }}>
                        <i className="bi bi-calendar-check" style={{ color: '#7c3aed' }}></i>
                        <strong style={{ color: '#374151' }}>{formatDate(fecha)}</strong>
                        <span style={{ color: '#6b7280' }}>— {citasPorFecha[fecha].join(', ')}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  className="btn btn-outline-secondary"
                  onClick={handleRescan}
                  style={{ flex: 1 }}
                >
                  <i className="bi bi-arrow-counterclockwise me-2"></i>
                  Escanear otra
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleImport}
                  disabled={totalItemsParaImportar === 0 || importing}
                  style={{ flex: 1 }}
                >
                  <i className="bi bi-check-lg me-2"></i>
                  {importing ? 'Importando...' : `Importar ${totalItemsParaImportar} ${totalItemsParaImportar === 1 ? 'registro' : 'registros'}`}
                </button>
              </div>
            </>
          )}

          {/* Step: Importing */}
          {step === 'importing' && (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div className="spinner-border text-primary mb-3" role="status" style={{ width: '48px', height: '48px' }}>
                <span className="visually-hidden">Importando...</span>
              </div>
              <p style={{ color: '#666', fontSize: '14px' }}>
                Importando vacunas...
              </p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .import-cartilla-overlay {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          background-color: rgba(0, 0, 0, 0.5) !important;
          display: flex !important;
          justify-content: center !important;
          align-items: center !important;
          z-index: 100001 !important;
          padding: 20px !important;
        }

        .import-cartilla-modal {
          background: #fff !important;
          border-radius: 12px !important;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
          width: 100% !important;
          max-width: 520px !important;
          max-height: 90vh !important;
          display: flex !important;
          flex-direction: column !important;
          overflow: hidden !important;
        }

        .import-cartilla-modal .modal-header {
          display: flex !important;
          justify-content: space-between !important;
          align-items: center !important;
          padding: 16px 20px !important;
          border-bottom: 1px solid #eee !important;
          background: linear-gradient(135deg, #4CAF50 0%, #7AB43B 100%) !important;
        }

        .import-cartilla-modal .modal-header h3 {
          margin: 0 !important;
          font-size: 18px !important;
          font-weight: 600 !important;
          color: #fff !important;
        }

        .import-cartilla-modal .modal-close {
          background: rgba(255,255,255,0.2) !important;
          border: none !important;
          font-size: 24px !important;
          cursor: pointer !important;
          color: #fff !important;
          padding: 0 !important;
          line-height: 1 !important;
          width: 32px !important;
          height: 32px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          border-radius: 50% !important;
        }

        .import-cartilla-modal .modal-close:hover {
          background: rgba(255,255,255,0.3) !important;
        }

        .import-cartilla-modal .modal-body {
          flex: 1 !important;
          overflow-y: auto !important;
        }

        @media (max-width: 767px) {
          .import-cartilla-overlay {
            padding: 0 !important;
            /* Safe areas para el overlay */
            padding-top: env(safe-area-inset-top, 0px) !important;
            padding-bottom: env(safe-area-inset-bottom, 0px) !important;
            padding-left: env(safe-area-inset-left, 0px) !important;
            padding-right: env(safe-area-inset-right, 0px) !important;
          }

          .import-cartilla-modal {
            max-width: 100% !important;
            width: 100% !important;
            height: 100% !important;
            max-height: 100% !important;
            border-radius: 0 !important;
            margin: 0 !important;
          }

          .import-cartilla-modal .modal-header {
            padding-top: calc(16px + env(safe-area-inset-top, 0px)) !important;
            flex-shrink: 0 !important;
          }

          .import-cartilla-modal .modal-body {
            flex: 1 !important;
            overflow-y: auto !important;
            -webkit-overflow-scrolling: touch !important;
            padding-bottom: calc(20px + env(safe-area-inset-bottom, 0px)) !important;
          }
        }
      `}</style>
    </div>
  );
}
